import React, { useState, useEffect } from 'react';
import { useSEO } from '../hooks/useSEO';
import { toast } from 'sonner';
import { Heart, Building2, User, UserRound, Upload } from 'lucide-react';
import { api } from '../lib/api';
import { EditRegion } from '../components/AdminEditBar';
import { InlineText } from '../components/InlineEditable';

// Fallback default fields (used when the admin hasn't added any dynamic field yet).
const DEFAULT_BIREYSEL = [
  { key: 'name', label: 'Ad Soyad', type: 'text', required: true, col_span: 'half' },
  { key: 'phone', label: 'Telefon', type: 'tel', required: false, col_span: 'half' },
  { key: 'email', label: 'E-posta', type: 'email', required: true, col_span: 'half' },
  { key: 'area', label: 'İlgilendiğiniz Alan', type: 'select', required: false, col_span: 'half',
    options: 'Eğitim Desteği, Mentorluk, Atölye, Organizasyon, Medya & İletişim, Diğer' },
  { key: 'message', label: 'Mesajınız', type: 'textarea', required: false, col_span: 'full' },
];

const DEFAULT_KURUMSAL = [
  { key: 'company', label: 'Kurum Unvanı', type: 'text', required: true, col_span: 'half' },
  { key: 'sector', label: 'Çalışma Alanı', type: 'text', required: false, col_span: 'half' },
  { key: 'contact', label: 'İlgili Kişi', type: 'text', required: false, col_span: 'half' },
  { key: 'phone', label: 'Telefon', type: 'tel', required: false, col_span: 'half' },
  { key: 'email', label: 'E-posta', type: 'email', required: true, col_span: 'half' },
  { key: 'support', label: 'Vakfımıza Destek Vermek İstediğiniz Alan', type: 'textarea', required: false, col_span: 'full',
    placeholder: 'Aynî / nakdi destek, burs fonu, uzmanlık...' },
];

/** Turkish-safe slug from a label — used when admin leaves the key empty. */
function slugFromLabel(label) {
  const map = { ç: 'c', ğ: 'g', ı: 'i', ö: 'o', ş: 's', ü: 'u', Ç: 'c', Ğ: 'g', İ: 'i', I: 'i', Ö: 'o', Ş: 's', Ü: 'u' };
  return String(label || '')
    .replace(/[çğıöşüÇĞİIÖŞÜ]/g, ch => map[ch] || ch)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 60);
}

/** Normalize a content_* record into a field descriptor. */
function normField(item) {
  const d = item?.data || {};
  const key = (d.key || '').trim() || slugFromLabel(d.label) || item.id;
  return {
    key,
    label: d.label || '',
    type: d.type || 'text',
    required: !!d.required,
    col_span: d.col_span === 'full' ? 'full' : 'half',
    placeholder: d.placeholder || '',
    options: d.options || '',
    _id: item.id,
  };
}

function buildInitialState(fields) {
  const s = {};
  fields.forEach(f => { if (f.key) s[f.key] = ''; });
  return s;
}

export default function Volunteer() {
  useSEO('volunteer');
  const initialTab = (typeof window !== 'undefined' && window.location.hash === '#kurumsal') ? 'kurumsal' : 'bireysel';
  const [tab, setTab] = useState(initialTab);
  const [bireyselFields, setBireyselFields] = useState(DEFAULT_BIREYSEL);
  const [kurumsalFields, setKurumsalFields] = useState(DEFAULT_KURUMSAL);
  const [bireysel, setBireysel] = useState(() => buildInitialState(DEFAULT_BIREYSEL));
  const [kurumsal, setKurumsal] = useState(() => buildInitialState(DEFAULT_KURUMSAL));

  // React to hash changes (e.g. user clicks "Kurumsal Destek Formu" from Sponsors page)
  useEffect(() => {
    const onHashChange = () => {
      setTab(window.location.hash === '#kurumsal' ? 'kurumsal' : 'bireysel');
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  useEffect(() => {
    let alive = true;
    Promise.all([
      api.publicContent('volunteer_fields_bireysel').catch(() => []),
      api.publicContent('volunteer_fields_kurumsal').catch(() => []),
    ]).then(([b, k]) => {
      if (!alive) return;
      if (Array.isArray(b) && b.length > 0) {
        const f = b.map(normField).filter(x => x.key);
        setBireyselFields(f);
        setBireysel(buildInitialState(f));
      }
      if (Array.isArray(k) && k.length > 0) {
        const f = k.map(normField).filter(x => x.key);
        setKurumsalFields(f);
        setKurumsal(buildInitialState(f));
      }
    });
    return () => { alive = false; };
  }, []);

  const [errors, setErrors] = useState({});

  const submit = async (e, kind) => {
    e.preventDefault();
    const fields = kind === 'bireysel' ? bireyselFields : kurumsalFields;
    const values = kind === 'bireysel' ? bireysel : kurumsal;
    // Required check — collect all errors and show them inline under each field
    const errs = {};
    for (const f of fields) {
      if (f.required && !String(values[f.key] || '').trim()) {
        errs[f.key] = `"${f.label}" alanı zorunludur.`;
      }
    }
    setErrors(errs);
    const firstKey = Object.keys(errs)[0];
    if (firstKey) {
      const el = document.querySelector(`[data-testid="field-${firstKey}"]`);
      if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.focus({ preventScroll: true }); }
      return;
    }
    try {
      await api.createVolunteer({ ...values, type: kind });
      toast.success('Gönüllülük başvurunuz alındı. Teşekkür ederiz!');
      if (kind === 'bireysel') setBireysel(buildInitialState(bireyselFields));
      else setKurumsal(buildInitialState(kurumsalFields));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Başvuru gönderilemedi.');
    }
  };

  const setValue = (kind, key, val) => {
    if (kind === 'bireysel') setBireysel(prev => ({ ...prev, [key]: val }));
    else setKurumsal(prev => ({ ...prev, [key]: val }));
    setErrors(prev => (prev[key] ? { ...prev, [key]: '' } : prev));
  };

  return (
    <EditRegion label="Gönüllülük" to="/admin/volunteer-form-bireysel">
      <>
      <section className="relative bg-ink-600 text-cream overflow-hidden">
        <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: 'radial-gradient(circle at 80% 20%, #C9A96E 0, transparent 55%)' }} />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-24 md:py-28">
          <InlineText settingKey="volunteer_hero_kicker" fallback="Gönüllülük" as="div"
            className="text-[11px] tracking-[0.3em] uppercase text-gold font-semibold" />
          <InlineText settingKey="volunteer_hero_title" fallback={'Gönüllü <span class="italic text-coral-200">olur musunuz?</span>'} as="h1"
            html multiline className="font-display text-5xl md:text-6xl lg:text-7xl leading-[0.98] mt-5 max-w-4xl" />
          <InlineText settingKey="volunteer_hero_text" fallback="YEKÜV'ün Türkiye genelindeki eğitim ve kültür faaliyetlerinde gönüllü olmak isterseniz aşağıdaki formu doldurabilirsiniz." as="p"
            multiline className="mt-6 text-cream/75 text-base md:text-lg max-w-2xl" />
        </div>
      </section>

      <section id="kurumsal" className="bg-cream">
        <div className="max-w-[1000px] mx-auto px-6 lg:px-10 py-20 md:py-28">
          {/* Tabs */}
          <div className="inline-flex items-center p-1 bg-cream-200 rounded-full">
            <button onClick={() => setTab('bireysel')} className={`px-6 h-11 rounded-full text-sm font-medium inline-flex items-center gap-2 transition-colors ${tab === 'bireysel' ? 'bg-ink-600 text-cream' : 'text-ink-500'}`}><User className="w-4 h-4" /> Bireysel Gönüllülük</button>
            <button onClick={() => setTab('kurumsal')} className={`px-6 h-11 rounded-full text-sm font-medium inline-flex items-center gap-2 transition-colors ${tab === 'kurumsal' ? 'bg-ink-600 text-cream' : 'text-ink-500'}`}><Building2 className="w-4 h-4" /> Kurumsal Gönüllülük</button>
          </div>

          {tab === 'bireysel' && (
            <DynamicForm
              kind="bireysel"
              photoUpload
              title="Bireysel Gönüllülük"
              fields={bireyselFields}
              values={bireysel}
              setValue={setValue}
              onSubmit={submit}
              errors={errors}
              buttonIcon={<Heart className="w-4 h-4" />}
              buttonLabel="Gönder"
            />
          )}

          {tab === 'kurumsal' && (
            <DynamicForm
              kind="kurumsal"
              title="Kurumsal Gönüllülük"
              fields={kurumsalFields}
              values={kurumsal}
              setValue={setValue}
              onSubmit={submit}
              errors={errors}
              buttonIcon={<Building2 className="w-4 h-4" />}
              buttonLabel="Başvuruyu Gönder"
            />
          )}
        </div>
      </section>
    </>
    </EditRegion>
  );
}

function DynamicForm({ kind, title, fields, values, setValue, onSubmit, buttonIcon, buttonLabel, errors = {}, photoUpload = false }) {
  const halfFields = fields.filter(f => f.col_span !== 'full');
  const fullFields = fields.filter(f => f.col_span === 'full');
  const [uploading, setUploading] = React.useState(false);
  const fileRef = React.useRef(null);

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const r = await api.uploadVolunteerPhoto(file);
      setValue(kind, 'photo', r.url);
      toast.success('Fotoğraf yüklendi');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Fotoğraf yüklenemedi');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <form onSubmit={(e) => onSubmit(e, kind)} noValidate className="mt-8 bg-white rounded-3xl p-8 md:p-10 border border-ink-100 shadow-xl shadow-ink-900/5 fade-up" data-testid={`volunteer-form-${kind}`}>
      <h2 className="font-display text-3xl text-ink-600">{title}</h2>

      {photoUpload && (
        <div className="mt-6 flex items-center gap-4" data-testid="volunteer-photo-upload">
          <div className="w-20 h-20 rounded-full bg-cream-200 border border-ink-100 overflow-hidden grid place-items-center shrink-0">
            {values.photo ? (
              <img src={values.photo} alt="Profil" className="w-full h-full object-cover" />
            ) : (
              <UserRound className="w-8 h-8 text-ink-300" />
            )}
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-ink-400 font-semibold">Profil Fotoğrafı (opsiyonel)</div>
            <div className="mt-2 flex items-center gap-2">
              <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                data-testid="volunteer-photo-btn"
                className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-ink-600 text-cream text-xs font-semibold hover:bg-ink-500 disabled:opacity-60">
                <Upload className="w-3.5 h-3.5" /> {uploading ? 'Yükleniyor…' : 'Fotoğraf Yükle'}
              </button>
              {values.photo && (
                <button type="button" onClick={() => setValue(kind, 'photo', '')}
                  className="h-9 px-4 rounded-full border border-ink-200 text-ink-500 text-xs font-semibold hover:text-ink-600">Kaldır</button>
              )}
            </div>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadPhoto(e.target.files?.[0])} />
          </div>
        </div>
      )}

      {halfFields.length > 0 && (
        <div className="mt-6 grid sm:grid-cols-2 gap-4">
          {halfFields.map(f => (
            <FieldRenderer key={f.key} f={f} value={values[f.key] || ''} onChange={(v) => setValue(kind, f.key, v)} error={errors[f.key]} />
          ))}
        </div>
      )}

      {fullFields.map(f => (
        <div className="mt-4" key={f.key}>
          <FieldRenderer f={f} value={values[f.key] || ''} onChange={(v) => setValue(kind, f.key, v)} error={errors[f.key]} />
        </div>
      ))}

      <button className="mt-6 inline-flex items-center gap-2 bg-ink-600 text-cream rounded-full px-8 h-12 font-semibold hover:bg-ink-500" data-testid={`volunteer-submit-${kind}`}>
        {buttonIcon} {buttonLabel}
      </button>
    </form>
  );
}

function FieldRenderer({ f, value, onChange, error }) {
  const labelText = `${f.label}${f.required ? ' *' : ''}`;
  const label = (
    <label className="text-xs uppercase tracking-wider text-ink-400 font-semibold">{labelText}</label>
  );
  const borderCls = error ? 'border-red-400 focus:border-red-500' : 'border-ink-100 focus:border-ink-500';
  const errorMsg = error ? (
    <div className="mt-1.5 text-xs text-red-500 font-medium" data-testid={`field-error-${f.key}`}>{error}</div>
  ) : null;

  if (f.type === 'textarea') {
    return (
      <div>
        {label}
        <textarea rows={4} value={value} placeholder={f.placeholder || ''} onChange={e => onChange(e.target.value)}
          className={`mt-1.5 w-full p-4 rounded-xl bg-cream-200 border ${borderCls} outline-none text-ink-600`}
          data-testid={`field-${f.key}`} />
        {errorMsg}
      </div>
    );
  }

  if (f.type === 'select') {
    const opts = (f.options || '').split(',').map(s => s.trim()).filter(Boolean);
    return (
      <div>
        {label}
        <select value={value} onChange={e => onChange(e.target.value)}
          className={`mt-1.5 w-full h-12 px-4 rounded-xl bg-cream-200 border ${borderCls} outline-none text-ink-600`}
          data-testid={`field-${f.key}`}>
          <option value="">Seçiniz…</option>
          {opts.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
        {errorMsg}
      </div>
    );
  }

  // text | email | tel
  return (
    <div>
      {label}
      <input type={f.type} value={value} placeholder={f.placeholder || ''} onChange={e => onChange(e.target.value)}
        className={`mt-1.5 w-full h-12 px-4 rounded-xl bg-cream-200 border ${borderCls} focus:ring-2 focus:ring-ink-500/10 outline-none text-ink-600`}
        data-testid={`field-${f.key}`} />
      {errorMsg}
    </div>
  );
}
